// ── Server Agent wizard — pure command/format/state helpers (TPT65) ──
//
// DOM-free, DB-free — same discipline as lib/vcs-form.js/lib/task-app-link.js. Extracted
// so the wizard's string-building and status classification logic is unit-testable, and so
// api/web/src/pages/server-agent.js stays about rendering, not string math.
//
// The browser never calls GET /server-agent/script — see ai/architecture/tt-server-agent.md
// § Web wizard for why (no devices DELETE route anywhere in the API → a browser-side probe
// call would leave a permanent phantom devices row on every typo). This module instead
// builds the one-liner client-side from a project-scoped token minted via
// POST /api/auth/project-token, which the box uses to make the one real /script call itself.

// Agent choices for Step 1. `available: false` entries render disabled + "Under
// construction" — flipping one to `true` is the whole launch diff for that agent, no
// markup change needed. Colors match this app's OWN existing usage (styles.css
// .assignee-badge--pi is already #8a63f2), not the Task App's differently-tinted palette.
export const AGENTS = [
  { id: 'claude', label: 'Claude', color: '#d97757', available: true },
  { id: 'codex',  label: 'Codex',  color: '#10a37f', available: false },
  { id: 'pi',     label: 'Pi',     color: '#8a63f2', available: false },
];

// POSIX single-quote escape — client-side twin of the exact helper the route already uses
// server-side to embed values into the bootstrap script (api/src/routes/server-agent.js's
// shQuote). Wrap any value in single quotes after this and it survives verbatim in sh/bash.
export function shQuote(value) {
  return `'${String(value).replace(/'/g, `'"'"'`)}'`;
}

// encodeURIComponent leaves `' ! ( ) *` untouched — all still meaningful once the resulting
// URL string sits inside a single-quoted shell assignment (a literal `'` would close the
// quote early). Percent-encode those explicitly on top of the normal URI encoding.
export function encodeParam(value) {
  return encodeURIComponent(value).replace(/['!()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());
}

// Builds the GET /server-agent/script URL the remote box will fetch. Exactly one of
// deviceName/deviceId, mirroring the route's own both-or-neither 400 rule
// (api/src/routes/server-agent.js) — throws rather than emit a URL the server would reject.
export function buildScriptUrl({ origin, projectId, agent, deviceName, deviceId }) {
  const hasName = deviceName != null && deviceName !== '';
  const hasId = deviceId != null && deviceId !== '';
  if (hasName === hasId) {
    throw new Error('buildScriptUrl: pass exactly one of deviceName or deviceId');
  }
  const idPart = hasName ? `device_name=${encodeParam(deviceName)}` : `device_id=${encodeParam(deviceId)}`;
  return `${origin}/api/projects/${encodeParam(projectId)}/server-agent/script?agent=${encodeParam(agent)}&${idPart}`;
}

// Builds the full copy-pasteable one-liner. Token travels as an env-var assignment prefixed
// onto `bash -c`, NOT as a `-H` argv value — a bare `curl -H "Authorization: Bearer <jwt>"`
// puts the live token in curl's own argv, readable by any other user on the box via
// `ps aux` for as long as the process runs (the exact exposure the generated script's own
// comment says it avoids). An env-prefixed assignment to a command is not part of that
// command's argv; it only lands in /proc/<pid>/environ, which is owner-only. This does NOT
// hide the token from shell history — pasting any command containing a literal secret
// still writes it to .bash_history/.zsh_history regardless of argv vs env-prefix.
//
// `set -o pipefail` is load-bearing: `curl ... | bash` alone reports the *pipeline's* exit
// status (bash's, generally 0) even when curl fails with a 4xx (exit 22) — the failure
// would otherwise be silently swallowed.
export function buildAgentCommand({ origin, projectId, agent, token, deviceName, deviceId }) {
  const url = buildScriptUrl({ origin, projectId, agent, deviceName, deviceId });
  return `TT_TOKEN=${shQuote(token)} TT_URL=${shQuote(url)} bash -c 'set -o pipefail; curl -fsSL -H "Authorization: Bearer $TT_TOKEN" "$TT_URL" | bash'`;
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

function isPrivateIPv4(hostname) {
  const m = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const a = Number(m[1]), b = Number(m[2]);
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || a === 127;
}

// A command built on a non-publicly-reachable origin is worthless once pasted onto a
// remote box — that box can't reach `localhost:4454`. Flags loopback hosts, *.local/
// *.localhost mDNS names, bare no-dot hostnames (intranet-only), and RFC1918 literals.
// `insecure: true` separately flags plaintext http:// on an otherwise-public host, where
// the live token would cross the wire unencrypted.
export function isPublicOrigin(origin) {
  let url;
  try { url = new URL(origin); } catch { return { public: false, insecure: false }; }
  const host = url.hostname.toLowerCase();
  const isLoopbackOrPrivate = LOOPBACK_HOSTS.has(host)
    || host.endsWith('.local') || host.endsWith('.localhost')
    || !host.includes('.') || isPrivateIPv4(host);
  return {
    public: !isLoopbackOrPrivate,
    insecure: !isLoopbackOrPrivate && url.protocol === 'http:',
  };
}

// "just now" / "4 min ago" / "3 days ago" / null (no timestamp yet). No existing timeAgo()
// helper lives in utils.js (that module exports only formatDate, an absolute-date
// formatter) — kept here rather than added there since it's wizard-specific vocabulary.
export function relativeTime(iso, now = Date.now()) {
  if (!iso) return null;
  const diffMs = now - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return 'just now';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

// "in 6 days" / "in 20 min" / "expired" / null. Separate from relativeTime() above: that
// one is for last_seen_at (always past-or-now — a positive diff only means clock skew, so
// it clamps to "just now") while token_expires_at is legitimately in the future for most of
// its 7-day life. Reusing relativeTime() for both would either mis-clamp expiry to "just
// now" or mis-report last-seen skew as "in N minutes".
export function expiresIn(iso, now = Date.now()) {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - now;
  if (!Number.isFinite(diffMs)) return null;
  if (diffMs <= 0) return 'expired';
  const min = Math.floor(diffMs / 60000);
  if (min < 60) return `in ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `in ${hr} hr${hr === 1 ? '' : 's'}`;
  const day = Math.floor(hr / 24);
  return `in ${day} day${day === 1 ? '' : 's'}`;
}

// Classifies one GET /server-agent/status device row (shapeStatusRow() shape:
// { last_seen_at, token_expired, seen_recently, connected, ... }) into one of four
// mutually-exclusive UI states. `connected` and `seen_recently` are independent booleans
// in the API response (a device can be connected-but-idle, or briefly-seen-but-not-yet-
// past its very first checkin classification) — token_expired takes visual priority over
// both, since "renew now" is the actionable state regardless of recent activity.
export function deviceState(device, now = Date.now()) {
  if (!device.last_seen_at) return { key: 'waiting', label: 'Waiting for first check-in' };
  if (device.token_expired) return { key: 'expired', label: 'Token expired — renew' };
  if (device.connected && device.seen_recently) return { key: 'connected', label: 'Connected' };
  if (device.connected) return { key: 'idle', label: 'Idle' };
  return { key: 'waiting', label: 'Waiting for first check-in' };
}
